import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { OutputTemplateFormValues } from "~/models/outputTemplate/OutputTemplate";
import { useGetProvidersQuery } from "~/services/providers";
import { useGetscriptTypesQuery } from "~/services/script";
import { useCreateOutputTemplateMutation } from "~/services/outputTemplate";

export const useFormOutputTemplate = (
  onCloseModal: () => void,
  refetch: () => void,
  onSuccess?: (title: string, message: string, response: any) => void,
  onError?: (title: string, message: string) => void,
) => {
  const isSubmittingRef = useRef(false);
  const { t } = useTranslation();

  const { data: provider } = useGetProvidersQuery();
  const { data: scriptTypeData } = useGetscriptTypesQuery({
    page: 0,
    size: 9999,
    active: true,
  });

  const [createOutputTemplate] = useCreateOutputTemplateMutation();

  const {
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    control,
    watch,
  } = useForm<OutputTemplateFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      cloudProviderId: undefined,
      scriptTypeId: undefined,
      file: null,
    },
  });

  const currentScript = watch("file");

  const handleError = useCallback(
    (error: any) => {
      onError?.(
        `${error?.data?.title ?? t("Error")} (${error?.data?.status ?? ""})`,
        error?.data?.detail ?? t("UnexpectedError"),
      );
    },
    [onError, t],
  );

  const submitForm = useCallback(
    async (data: OutputTemplateFormValues) => {
      if (isSubmittingRef.current) return;

      isSubmittingRef.current = true;

      try {
        const scriptTypeId = typeof data.scriptTypeId === "object"
          ? Number(data.scriptTypeId?.value)
          : Number(data.scriptTypeId);

        const cloudProviderId = typeof data.cloudProviderId === "object"
          ? Number(data.cloudProviderId?.value)
          : Number(data.cloudProviderId);

        await createOutputTemplate({
          name: data.name.toUpperCase(),
          scriptTypeId,
          cloudProviderId,
          file: data.file as File,
        }).unwrap();

        refetch();

        onSuccess?.(
          t("MessageOperationCompletedSuceesuflly"),
          t("OutputTemplateCreatedSuccessfully"),
          undefined,
        );
        onCloseModal();
      } catch (error: any) {
        handleError(error);
      } finally {
        isSubmittingRef.current = false;
      }
    },
    [createOutputTemplate, refetch, onSuccess, onCloseModal, handleError, t],
  );

  return {
    handleSubmit,
    control,
    errors,
    isValid,
    isSubmitting,
    currentScript,
    submitForm,
    scriptTypeData,
    provider,
  };
};
